import { t } from 'ttag'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { api } from '@h2/api'
import { user as getUser } from '@h2/common/selectors/user'
import { Loader } from '@h2/components/loading/loader'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Page } from '@h2/common/components/page'
import { PB97T, PG10S, PB90T, WHITE } from '@h2/components/colors'
import { CollapseBottomWhitespace } from '@h2/common/components/layout'
import { Flex } from '@h2/components/layout/flex'
import college from '@h2/assets/svg/icons/college.svg'

import { MajorListing } from './components/majorListing'
import { About } from './components/about'
import { Admissions } from './components/admissions'
import { Enrollments } from './components/enrollments'
import { Tuition } from './components/tuition'
import { FinancialAid } from './components/financialAid'
import { StudentBody } from './components/studentBody'

import { NextSteps } from '../../opportunities/components/nextSteps'

const PaddedWrap = styled.div`
  padding: 15px;
`
const Section = styled.div`
  background-color: #fff;
  padding: 15px;
  width: 100%;
  margin: 15px 0 15px 0;
`
const TitleSection = styled(Section)`
  background-color: ${PG10S};
  margin-top: 0;
`
const TitleWrapper = styled.div`
  width: 100%;
`
const PageTitleWrapper = styled(TitleWrapper)`
  margin: -15px;
  min-height: 160px;
`
const StyledTitleImage = styled.div`
  display: flex;
  max-height: 200px;
  background-image: ${({ src }) => `url(${src})`};
  background-size: ${({ shouldContain }) => (shouldContain ? 'contain' : 'cover')};
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  padding: 6px;
  width: 30%;
  max-width: 300px;
  min-height: 160px;
  align-items: flex-end;
`
const TitleImage = ({ pic }) => {
  const fixUrl = url => {
    return dynamicValues.isProduction || url.startsWith('http')
      ? url
      : `https://app.headed2.com${url}`
  }

  return (
    <StyledTitleImage src={fixUrl(pic)} shouldContain={true}>
      <a
        href="https://clearbit.com"
        style={{
          backgroundColor: 'rgba(0,0,0,.4)',
          fontSize: '12px',
          color: 'white',
          display: 'block',
          width: '100%',
          textAlign: 'center'
        }}>
        {t`Logo provided by Clearbit`}
      </a>
    </StyledTitleImage>
  )
}
const OccupationDetail = () => {
  const { institutionId } = useParams()
  const userInfo = useSelector(getUser)
  const { data: collegeDetails, isLoading } = useQuery(
    ['college-detail', institutionId],
    () => {
      return api.fetch(`/colleges/profile/${institutionId}`, {
        method: 'GET',
        version: 'v1'
      })
    }
  )

  if (isLoading) {
    return <Loader />
  }

  if (!collegeDetails) {
    return <Text>{t`Could not locate institution ${institutionId}`}</Text>
  }

  const fallbackCollegeImage = college
  const collegeImageUrl = collegeDetails?.domain
    ? `/images/institution/${collegeDetails.id}/logo.png`
    : fallbackCollegeImage
  const OutputSection = ({ value, title }) => {
    if (!value) {
      return null
    }

    return (
      <Section>
        <TitleWrapper>
          <Heading level="4" fontSize="30px">
            {title}
          </Heading>
          {value}
        </TitleWrapper>
      </Section>
    )
  }

  return (
    <>
      <Page bgColor={PB97T}>
        <Page.Layout>
          <Page.Content>
            <TitleSection>
              <PageTitleWrapper>
                <Flex gap="25px">
                  <TitleImage pic={collegeImageUrl} />
                  <div style={{ padding: '15px' }}>
                    <Heading level="2" fontSize="30px" color={WHITE}>
                      {collegeDetails.name}
                    </Heading>
                  </div>
                </Flex>
              </PageTitleWrapper>
            </TitleSection>
            <OutputSection
              title={t`About this Institution`}
              value={<About profile={collegeDetails} />}
            />

            <OutputSection
              title={t`Admissions`}
              value={<Admissions admissions={collegeDetails.admissions} />}
            />
            <OutputSection
              title={t`Enrollments`}
              value={<Enrollments enrollments={collegeDetails.enrollments} />}
            />
            <OutputSection
              title={t`Tuition`}
              value={<Tuition tuition={collegeDetails.tuition} />}
            />

            <OutputSection
              title={t`Financial Aid`}
              value={<FinancialAid aid={collegeDetails.financialAid} />}
            />
            <Flex gap="15px">
              <OutputSection
                title={t`Programs Offered`}
                value={<MajorListing majors={collegeDetails.majors || []} />}
              />
              <OutputSection
                title={t`Student Body`}
                value={<StudentBody demographics={collegeDetails.demographics} />}
              />
            </Flex>
          </Page.Content>
          <Page.Sidebar bgColor={PB90T}>
            <PaddedWrap>
              <NextSteps
                objectId={institutionId}
                objectType={'college'}
                user={userInfo}
                recommendations={collegeDetails.recomendations}
              />
            </PaddedWrap>
          </Page.Sidebar>
        </Page.Layout>
      </Page>
      <CollapseBottomWhitespace />
    </>
  )
}

export { OccupationDetail }
